// src/api/auth.ts
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import type { AuthResponse, User } from '../types';

// ---------------------------------------------------------------------------
// Base URLs
// ---------------------------------------------------------------------------
const getBaseUrl = (): string => {
    if (import.meta.env.DEV) return `http://${window.location.hostname}:8000`;
    return 'https://api.thewrighttechdepot.com';
};

export const API_BASE_URL = import.meta.env.VITE_API_URL || `${getBaseUrl()}/api`;
export const MEDIA_BASE_URL = import.meta.env.VITE_MEDIA_URL || getBaseUrl();

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface LoginResponse {
    access: string;
    refresh: string;
    user?: User;
    roles?: string[];
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------
let isRefreshing = false;
let currentInterceptors: { request: number; response: number } | null = null;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token)));
    failedQueue = [];
};

const getAccessToken = (): string | null => localStorage.getItem('access_token');
const getRefreshToken = (): string | null => localStorage.getItem('refresh_token');

const setTokens = (access: string, refresh?: string | null) => {
    localStorage.setItem('access_token', access);
    if (refresh) localStorage.setItem('refresh_token', refresh);
};

const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
};

const tokenNeedsRefresh = (thresholdMs = 5 * 60 * 1000): boolean => {
    const token = getAccessToken();
    if (!token) return false;
    try {
        const { exp } = jwtDecode<{ exp: number }>(token);
        return exp * 1000 - Date.now() < thresholdMs;
    } catch {
        return false;
    }
};

// ---------------------------------------------------------------------------
// Refresh logic — single source of truth
// ---------------------------------------------------------------------------
const refreshTokenRequest = async (
    refresh: string,
): Promise<{ access: string; refresh?: string }> => {
    // bare axios — skip interceptors
    const res = await axios.post<{ access: string; refresh?: string }>(
        `${API_BASE_URL}/auth/token/refresh/`,
        { refresh },
    );
    return res.data;
};

const refreshOnce = async (): Promise<string> => {
    if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve: t => resolve(t as string), reject });
        });
    }

    isRefreshing = true;
    try {
        const refresh = getRefreshToken();
        if (!refresh) throw new Error('No refresh token');
        const data = await refreshTokenRequest(refresh);
        setTokens(data.access, data.refresh ?? null);
        processQueue(null, data.access);
        return data.access;
    } catch (err) {
        processQueue(err, null);
        clearTokens();
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw err;
    } finally {
        isRefreshing = false;
    }
};

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------
const setupAuthInterceptors = () => {
    if (currentInterceptors) {
        apiClient.interceptors.request.eject(currentInterceptors.request);
        apiClient.interceptors.response.eject(currentInterceptors.response);
    }

    const requestInterceptor = apiClient.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
            const url = config.url ?? '';
            const isAuthEndpoint =
                url.includes('/auth/login/') ||
                url.includes('/auth/register/') ||
                url.includes('/auth/token/refresh/') ||
                url.includes('/auth/google/callback/');
            if (isAuthEndpoint) return config;

            try {
                const token = getAccessToken();
                if (token) {
                    // proactive refresh if close to expiry
                    const valid = tokenNeedsRefresh() ? await refreshOnce() : token;
                    config.headers.Authorization = `Bearer ${valid}`;
                }
            } catch {
                // fall through — server will 401
            }
            return config;
        },
        (err: AxiosError) => Promise.reject(err),
    );

    const responseInterceptor = apiClient.interceptors.response.use(
        res => res,
        async (error: AxiosError) => {
            const original = error.config as
                InternalAxiosRequestConfig & { _retry?: boolean };

            if (error.response?.status !== 401 || original._retry) {
                return Promise.reject(error);
            }

            const url = original.url ?? '';
            if (
                url.includes('/auth/login/') ||
                url.includes('/auth/token/refresh/')
            ) {
                return Promise.reject(error);
            }

            original._retry = true;

            try {
                const newToken = await refreshOnce();
                original.headers.Authorization = `Bearer ${newToken}`;
                return apiClient(original);
            } catch (refreshErr) {
                return Promise.reject(refreshErr);
            }
        },
    );

    currentInterceptors = {
        request: requestInterceptor,
        response: responseInterceptor,
    };
    return currentInterceptors;
};
const extractError = (err: unknown, fallback: string): string => {
    if (!axios.isAxiosError(err)) {
        return err instanceof Error ? err.message : fallback;
    }
    const data = err.response?.data as Record<string, unknown> | string | undefined;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
        // detail is usually a string
        if (typeof data.detail === 'string') return data.detail;
        // error can be string OR array
        if (typeof data.error === 'string') return data.error;
        if (Array.isArray(data.error) && typeof data.error[0] === 'string') return data.error[0];
        if (typeof data.message === 'string') return data.message;
        // field errors
        const firstField = Object.keys(data)[0];
        const firstVal = firstField ? data[firstField] : null;
        if (Array.isArray(firstVal) && typeof firstVal[0] === 'string') return firstVal[0];
        if (typeof firstVal === 'string') return firstVal;
    }
    return fallback;
};
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const authAPI = {
    /**
     * Restore any existing session on app boot.
     * Returns true if tokens exist and interceptors are installed.
     */
    restoreSession(): boolean {
        const access = getAccessToken();
        const refresh = getRefreshToken();
        if (!access && !refresh) return false;
        setupAuthInterceptors();
        return true;
    },

    setupInterceptors() {
        return setupAuthInterceptors();
    },

    removeInterceptors() {
        if (currentInterceptors) {
            apiClient.interceptors.request.eject(currentInterceptors.request);
            apiClient.interceptors.response.eject(currentInterceptors.response);
            currentInterceptors = null;
        }
    },


    async login(email: string, password: string, rememberMe: boolean = true): Promise<AuthResponse> {
        try {
            const res = await apiClient.post<AuthResponse>('/auth/login/', { email, password });
            const { access, refresh } = res.data;

            if (rememberMe) sessionStorage.removeItem('ephemeral_session');
            else sessionStorage.setItem('ephemeral_session', '1');

            setTokens(access, refresh);
            setupAuthInterceptors();
            return res.data;
        } catch (err) {
            throw new Error(extractError(err, 'Login failed'));
        }
    },

    async register(userData: {
        email: string;
        password: string;
        first_name?: string;
        last_name?: string;
        phone?: string;
        [key: string]: any;
    }): Promise<{ access: string; refresh: string; user?: User; roles?: string[] }> {
        try {
            const res = await apiClient.post('/auth/register/', userData);
            const { access, refresh } = res.data;
            if (access && refresh) {
                setTokens(access, refresh);
                setupAuthInterceptors();
            }
            return res.data;
        } catch (err) {
            throw new Error(extractError(err, 'Registration failed'));
        }
    },

    async googleLogin(idToken: string, rememberMe: boolean = true): Promise<{
        access: string;
        refresh: string;
        user?: User;
        roles?: string[];
    }> {
        try {
            const res = await apiClient.post('/auth/google/callback/', {
                access_token: idToken,
                backend: 'google-oauth2',
            });
            const { access, refresh } = res.data;

            if (rememberMe) sessionStorage.removeItem('ephemeral_session');
            else sessionStorage.setItem('ephemeral_session', '1');

            setTokens(access, refresh);
            setupAuthInterceptors();
            return res.data;
        } catch (err) {
            throw new Error(extractError(err, 'Google login failed'));
        }
    },

    async me(): Promise<User & { roles?: string[] }> {
        const res = await apiClient.get<User & { roles?: string[] }>('/auth/me/');
        return res.data;
    },

    async logout(): Promise<void> {
        const refresh = getRefreshToken();
        if (refresh) {
            try {
                await apiClient.post('/auth/logout/', { refresh });
            } catch {
                // ignore
            }
        }
        clearTokens();
        if (currentInterceptors) {
            apiClient.interceptors.request.eject(currentInterceptors.request);
            apiClient.interceptors.response.eject(currentInterceptors.response);
            currentInterceptors = null;
        }
    },

    async initiatePasswordChange(payload: {
        current_password: string;
        channel: 'email' | 'whatsapp';
    }): Promise<{ request_id: string; channel: string; masked_destination: string }> {
        const res = await apiClient.post('/auth/change-password/initiate/', payload);
        return res.data;
    },

    async confirmPasswordChange(payload: {
        request_id: string;
        otp: string;
        new_password: string;
    }): Promise<{ success: boolean }> {
        const res = await apiClient.post('/auth/change-password/confirm/', payload);
        return res.data;
    },
};